import type { IAccrualSettingsResponse } from "@redbelly-tokeniser/mystake-api-sdk";
import React from "react";
import { Alert, Button, Loader } from "../../../../../designSystem";
import { isYieldOffered } from "../../../pdtiFeatureFlags";
import { useAccrualSettings } from "../../../settings/yield/useAccrualSettings";
import { useCompanyContext } from "../../../useCompanyContext";

export interface IUseYieldFund {
  /** Every class in the fund bears yield and is held at $1.0000. */
  yieldFund: boolean;
  settings: IAccrualSettingsResponse | null;
  isLoading: boolean;
  loadError: string | null;
  reload: () => void;
}

/**
 * Whether the fund bears yield. A company not offered yield makes no request and is never a yield fund,
 * so the screens that ask behave exactly as they did before yield existed.
 */
export const useYieldFund = (
  routeCompanyId?: string,
  // False for a viewer who cannot read the yield settings: no request, and never a yield fund.
  { enabled = true }: { enabled?: boolean } = {}
): IUseYieldFund => {
  const { companyId: contextCompanyId, features, registryType } = useCompanyContext();
  const companyId = routeCompanyId ?? contextCompanyId;
  const yieldOffered = enabled && isYieldOffered(features ?? {}, registryType);
  const { settings, isLoading, loadError, reload } = useAccrualSettings(companyId, { enabled: yieldOffered });
  return {
    yieldFund: yieldOffered && settings?.accrues === true,
    settings,
    isLoading: yieldOffered && isLoading,
    loadError: yieldOffered ? loadError : null,
    reload,
  };
};

/**
 * Holds a movement form back until the fund's yield setting is known. Rendering the form without it
 * would offer a price choice that a yield fund cannot honour.
 */
export const YieldFundGate: React.FC<{ state: IUseYieldFund; children: React.ReactNode }> = ({ state, children }) => {
  if (state.isLoading) {
    return <Loader variant="section" />;
  }
  if (state.loadError) {
    return (
      <div data-testid="yield-fund-load-error">
        <Alert tone="error" title="Couldn't load the fund's yield settings">
          {state.loadError}
        </Alert>
        <Button variant="secondary" size="sm" onClick={state.reload} style={{ marginTop: 12 }}>
          Try again
        </Button>
      </div>
    );
  }
  return <>{children}</>;
};
